/**
 * «Добавить культуру» — завести запись справочника из системного каталога.
 *
 * Единственное write-действие, которое вкладка «Справочник» может предложить
 * СЕГОДНЯ: бэкенд даёт `POST /reference/tenant-crops/from-canon` (идемпотентно —
 * культура уже в номенклатуре вернёт существующую строку, дубля не будет).
 *
 * Правка/архив/слияние дублей на этом экране намеренно НЕ рисуются: у
 * `local_catalog_items` нет ни `archived_at`, ни тенант-merge сервиса, и кнопка
 * вела бы в никуда. Это честный долг, он выписан в описании PR, а не спрятан
 * под неработающей кнопкой.
 *
 * Список — всегда по алфавиту, без «часто используемых» сверху (сквозной
 * UX-канон ФМС: ищут глазами по букве или печатают).
 */
import { useEffect, useState } from 'react';
import { Check, Loader2, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { ApiError } from '@/lib/api';
import { cropLabel } from '@/lib/cropLabel';
import {
  useAdoptCanonCrop,
  useCanonSearch,
  useRequestCanonCrop,
} from '@/lib/reference/tenantCrops';
import { useFirstCompanyId } from './CatalogSection';

function errMsg(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.detail : fallback;
}

export function AddCropFromCanonDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);
  const search = useCanonSearch(query);
  const adopt = useAdoptCanonCrop();
  const request = useRequestCanonCrop();
  // Передаём компанию явно: без неё бэкенд в мультикомпанийном тенанте
  // отвечает 422 с API-жаргоном («укажите company_id»), который человеку на
  // этом экране ничего не говорит.
  const companyId = useFirstCompanyId();

  useEffect(() => {
    if (open) {
      setQuery('');
      setPicked(null);
    }
  }, [open]);

  const options = (search.data ?? [])
    .map((c) => ({ id: c.id, name: cropLabel(c) }))
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'));

  function handleRequest() {
    const name = query.trim();
    if (!name) return;
    request.mutate(
      { name },
      {
        onSuccess: () => {
          toast.info(`Заявка на «${name}» отправлена`);
          onOpenChange(false);
        },
        onError: (e) => toast.error(errMsg(e, 'Не удалось отправить заявку')),
      },
    );
  }

  function handleAdd() {
    if (!picked) return;
    adopt.mutate(
      { cropId: picked.id, companyId },
      {
        onSuccess: () => {
          toast.success(`«${picked.name}» добавлена в справочник`);
          onOpenChange(false);
        },
        onError: (e) => toast.error(errMsg(e, 'Не удалось добавить культуру')),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Добавить культуру</DialogTitle>
          <DialogDescription>
            Выберите культуру из общего справочника — она появится в вашем списке и
            в выпадашке при заведении посева.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Поиск по названию и народным названиям…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                // Сменил запрос — выбор снимается: иначе «Добавить» применило бы
                // невидимую в текущей выдаче культуру.
                setPicked(null);
              }}
            />
          </div>

          <div className="max-h-64 overflow-y-auto rounded-lg border border-border">
            {query.trim().length < 2 ? (
              <p className="p-4 text-sm text-muted-foreground">
                Введите хотя бы две буквы названия.
              </p>
            ) : search.isLoading ? (
              <div className="flex flex-col gap-2 p-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : options.length === 0 ? (
              /* Макет, состояние 5, ветка Б: «в системном справочнике такого
                 нет» — это НЕ тупик. Хозяйство вправе вести культуру вне
                 госкаталога, поэтому здесь равноправная кнопка, а не мелкая
                 ссылка. Раньше окно просто говорило «ничего не нашли» и
                 оставляло «Добавить» серой — UAT founder 08.08. */
              <div className="flex flex-col items-start gap-2 p-4">
                <p className="text-sm text-muted-foreground">
                  В общем справочнике такого нет.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={request.isPending}
                  onClick={handleRequest}
                >
                  {request.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Попросить добавить «{query.trim()}» в общий справочник
                </Button>
                <p className="text-xs text-muted-foreground">
                  Заявка уйдёт нам; вы получите ответ, когда культуру заведут.
                </p>
              </div>
            ) : (
              <ul>
                {options.map((o) => (
                  <li key={o.id}>
                    <button
                      type="button"
                      aria-pressed={picked?.id === o.id}
                      onClick={() => setPicked(o)}
                      className={cn(
                        'flex min-h-10 w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm',
                        picked?.id === o.id ? 'bg-primary/10 text-foreground' : 'hover:bg-accent',
                      )}
                    >
                      <span className="font-medium">{o.name}</span>
                      {picked?.id === o.id && <Check className="h-4 w-4 text-primary" />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button disabled={!picked || adopt.isPending} onClick={handleAdd}>
              {adopt.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Добавить
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AddCropFromCanonDialog;
